import React, { useState, useEffect, useRef } from 'react';
import { getImageUrl } from '../utils/imageUrl';
import config from '../config';
import { useNavigate, useParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { detailAnnonce, modifierAnnonce } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Upload, X, Trash2, Car } from 'lucide-react';

const ModifierAnnonce = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [erreur, setErreur] = useState('');
  const [photosExistantes, setPhotosExistantes] = useState([]);
  const [nouvellesPhotos, setNouvellesPhotos] = useState([]);
  const [previews, setPreviews] = useState([]);
  const fileInputRef = useRef(null);
  const [form, setForm] = useState({
    titre: '', marque: '', modele: '', annee: '',
    kilometrage: '', carburant: '', transmission: '',
    prix: '', negociable: false, description: '', localisation: '', statut: 'active'
  });

  useEffect(() => {
    detailAnnonce(id).then(res => {
      const a = res.data;
      setForm({
        titre: a.titre, marque: a.marque, modele: a.modele,
        annee: a.annee, kilometrage: a.kilometrage, carburant: a.carburant,
        transmission: a.transmission, type_vehicule: a.type_vehicule || '',
        prix: a.prix, negociable: a.negociable,
        description: a.description || '', localisation: a.localisation || '',
        statut: a.statut
      });
      if (a.photos && a.photos.filter(p => p !== null).length > 0) {
        setPhotosExistantes(a.photos.filter(p => p !== null));
      }
    });
  }, [id]);

  if (!user) { navigate('/connexion'); return null; }

  const handleNouvellesPhotos = (e) => {
    const files = Array.from(e.target.files);
    setNouvellesPhotos(files);
    setPreviews(files.map(file => URL.createObjectURL(file)));
  };

  const supprimerPhotoExistante = async (url) => {
    try {
      const res = await fetch(`${config.API_URL}/api/photos/${id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await res.json();
      const photo = data.photos.find(p => p.url === url);
      if (photo) {
        await fetch(`${config.API_URL}/api/photos/${photo.id}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        setPhotosExistantes(photosExistantes.filter(p => p !== url));
      }
    } catch (err) {
      console.error(err);
    }
  };

  const retirerNouvellePhoto = (index) => {
    setNouvellesPhotos(nouvellesPhotos.filter((_, i) => i !== index));
    setPreviews(previews.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErreur('');
    try {
      await modifierAnnonce(id, form);
      if (nouvellesPhotos.length > 0) {
        const formData = new FormData();
        nouvellesPhotos.forEach(photo => formData.append('photos', photo));
        formData.append('est_principale', photosExistantes.length === 0 ? 'true' : 'false');
        await fetch(`${config.API_URL}/api/photos/${id}`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          body: formData
        });
      }
      navigate(`/annonces/${id}`);
    } catch (err) {
      setErreur(err.response?.data?.message || 'Erreur lors de la modification');
    } finally {
      setLoading(false);
    }
  };

  const inputClass = "w-full border border-gray-200 rounded-xl px-4 py-2 text-sm focus:outline-none focus:border-yellow-400 transition bg-white";
  const labelClass = "block text-xs font-semibold text-gray-500 mb-1";

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      {/* Header */}
      <div style={{ background: '#0A0A0A' }} className="py-8 px-4">
        <div className="max-w-3xl mx-auto flex items-center gap-3">
          <div style={{ background: '#F5A62320' }} className="w-10 h-10 rounded-xl flex items-center justify-center">
            <Car size={20} style={{ color: '#F5A623' }} />
          </div>
          <div>
            <h1 className="text-white text-2xl font-black">Modifier l'annonce</h1>
            <p className="text-gray-400 text-sm">Mettez à jour les informations de votre véhicule</p>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8">

        {erreur && (
          <div className="bg-red-50 border border-red-200 text-red-600 p-4 rounded-2xl mb-6 text-sm font-semibold">
            ⚠️ {erreur}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">

          {/* Infos véhicule */}
          <div className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100">
            <h2 className="font-black text-gray-900 mb-4 flex items-center gap-2">
              <span style={{ color: '#F5A623' }}>|</span> Informations du véhicule
            </h2>
            <div className="space-y-4">
              <div>
                <label className={labelClass}>Titre</label>
                <input type="text" required value={form.titre}
                  onChange={(e) => setForm({ ...form, titre: e.target.value })}
                  className={inputClass}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Marque</label>
                  <input type="text" required value={form.marque}
                    onChange={(e) => setForm({ ...form, marque: e.target.value })}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Modèle</label>
                  <input type="text" required value={form.modele}
                    onChange={(e) => setForm({ ...form, modele: e.target.value })}
                    className={inputClass}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Année</label>
                  <input type="number" required value={form.annee}
                    onChange={(e) => setForm({ ...form, annee: e.target.value })}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Kilométrage</label>
                  <input type="number" required value={form.kilometrage}
                    onChange={(e) => setForm({ ...form, kilometrage: e.target.value })}
                    className={inputClass}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Carburant</label>
                  <select required value={form.carburant}
                    onChange={(e) => setForm({ ...form, carburant: e.target.value })}
                    className={inputClass}
                  >
                    <option value="">Sélectionner</option>
                    <option value="essence">Essence</option>
                    <option value="diesel">Diesel</option>
                    <option value="electrique">Électrique</option>
                    <option value="hybride">Hybride</option>
                    <option value="gpl">GPL</option>
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Transmission</label>
                  <select required value={form.transmission}
                    onChange={(e) => setForm({ ...form, transmission: e.target.value })}
                    className={inputClass}
                  >
                    <option value="">Sélectionner</option>
                    <option value="manuelle">Manuelle</option>
                    <option value="automatique">Automatique</option>
                  </select>
                </div>
                <div>
                    <label className={labelClass}>Type de véhicule</label>
                    <select value={form.type_vehicule}
                      onChange={(e) => setForm({ ...form, type_vehicule: e.target.value })}
                      className={inputClass}
                    >
                      <option value="">Sélectionner</option>
                      <option value="berline">Berline</option>
                      <option value="suv">SUV</option>
                      <option value="4x4">4x4</option>
                      <option value="pickup">Pick-up</option>
                      <option value="utilitaire">Utilitaire</option>
                      <option value="engin">Engin</option>
                      <option value="citadine">Citadine</option>
                      <option value="monospace">Monospace</option>
                    </select>
                  </div>
              </div>
            </div>
          </div>

          {/* Prix */}
          <div className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100">
            <h2 className="font-black text-gray-900 mb-4 flex items-center gap-2">
              <span style={{ color: '#F5A623' }}>|</span> Prix
            </h2>
            <div className="flex gap-4 items-center">
              <div className="flex-1">
                <label className={labelClass}>Prix (FCFA)</label>
                <input type="number" required value={form.prix}
                  onChange={(e) => setForm({ ...form, prix: e.target.value })}
                  className={inputClass}
                />
              </div>
              <div className="flex items-center gap-2 mt-4">
                <input type="checkbox" id="negociable" checked={form.negociable}
                  onChange={(e) => setForm({ ...form, negociable: e.target.checked })}
                  className="w-4 h-4 accent-yellow-400"
                />
                <label htmlFor="negociable" className="text-sm text-gray-600 font-semibold">Négociable</label>
              </div>
            </div>
          </div>

          {/* Statut */}
          <div className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100">
            <h2 className="font-black text-gray-900 mb-4 flex items-center gap-2">
              <span style={{ color: '#F5A623' }}>|</span> Statut
            </h2>
            <select value={form.statut}
              onChange={(e) => setForm({ ...form, statut: e.target.value })}
              className={inputClass}
            >
              <option value="active">Active</option>
              <option value="vendue">Vendue</option>
              <option value="expiree">Expirée</option>
            </select>
          </div>

          {/* Description */}
          <div className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100">
            <h2 className="font-black text-gray-900 mb-4 flex items-center gap-2">
              <span style={{ color: '#F5A623' }}>|</span> Description & Localisation
            </h2>
            <div className="space-y-4">
              <div>
                <label className={labelClass}>Description</label>
                <textarea value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={4} className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Localisation</label>
                <input type="text" value={form.localisation}
                  onChange={(e) => setForm({ ...form, localisation: e.target.value })}
                  className={inputClass}
                />
              </div>
            </div>
          </div>

          {/* Photos existantes */}
          <div className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100">
            <h2 className="font-black text-gray-900 mb-4 flex items-center gap-2">
              <span style={{ color: '#F5A623' }}>|</span> Photos actuelles
            </h2>
            {photosExistantes.length > 0 ? (
              <div className="grid grid-cols-3 gap-3">
                {photosExistantes.map((url, index) => (
                  <div key={index} className="relative">
                    {url?.includes('.mp4') || url?.includes('.mov') ? (
                      <div className="w-full h-32 bg-gray-800 rounded-xl flex items-center justify-center">
                        <span className="text-white text-3xl">▶</span>
                        <span className="text-gray-300 text-xs ml-2">Vidéo</span>
                      </div>
                    ) : (
                      <img
                        src={getImageUrl(url)}
                        alt={`apercu ${index + 1}`}
                        className="w-full h-32 object-cover rounded-xl"
                      />
                    )}
                    <button
                      type="button"
                      onClick={() => supprimerPhotoExistante(url)}
                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-400 text-sm">Aucune photo</p>
            )}
          </div>

          {/* Ajouter photos */}
          <div className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100">
            <h2 className="font-black text-gray-900 mb-4 flex items-center gap-2">
              <span style={{ color: '#F5A623' }}>|</span> Ajouter des photos
            </h2>
            <div
              onClick={() => fileInputRef.current.click()}
              style={{ border: '2px dashed #e5e7eb' }}
              className="rounded-2xl p-6 text-center cursor-pointer hover:border-yellow-400 transition"
            >
              <Upload size={32} className="mx-auto text-gray-300 mb-2" />
              <p className="text-gray-500 text-sm font-semibold">Cliquez pour ajouter des photos ou vidéos</p>
              <p className="text-xs text-gray-400 mt-1">Photos (JPG, PNG, WEBP) et Vidéos (MP4) — 50MB max par fichier</p>
            </div>
            <input ref={fileInputRef} type="file" multiple
              accept="image/*,video/*" onChange={handleNouvellesPhotos} className="hidden"
            />
            {previews.length > 0 && (
              <div className="grid grid-cols-3 gap-3 mt-4">
                {previews.map((preview, index) => (
                  <div key={index} className="relative">
                    {nouvellesPhotos[index]?.type?.startsWith('video/') ? (
                      <video src={preview} className="w-full h-32 object-cover rounded-xl" controls />
                    ) : (
                      <img src={preview} alt={`apercu ${index + 1}`} className="w-full h-32 object-cover rounded-xl" />
                    )}
                    <button type="button" onClick={() => retirerNouvellePhoto(index)}
                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-0.5 hover:bg-red-600"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Boutons */}
          <div className="flex gap-4">
            <button type="button" onClick={() => navigate(`/annonces/${id}`)}
              className="flex-1 bg-white border border-gray-200 text-gray-600 py-3 rounded-2xl font-bold hover:bg-gray-50 transition"
            >
              Annuler
            </button>
            <button type="submit" disabled={loading}
              style={{ background: '#F5A623' }}
              className="flex-1 text-black py-3 rounded-2xl font-black hover:opacity-90 transition disabled:opacity-50"
            >
              {loading ? 'Modification...' : 'Sauvegarder'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ModifierAnnonce;